import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  'https://kaphy-insurance-api-24bfecb5d4bb.herokuapp.com';

const SESSION_KEY = 'kaphy_sid';
const VISITOR_KEY = 'kaphy_vid';

const getSessionId = () => {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

const getVisitorId = () => {
  let vid = localStorage.getItem(VISITOR_KEY);
  if (!vid) {
    vid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(VISITOR_KEY, vid);
  }
  return vid;
};

const useSocketTracker = () => {
  const location = useLocation();
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const socketRef = useRef(null);
  // Refs so the connect handler always reads the latest values
  const userRef = useRef(user);
  const pageRef = useRef(location.pathname);
  const isAdminRef = useRef(isAdmin || isSuperAdmin);

  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { pageRef.current = location.pathname; }, [location.pathname]);
  useEffect(() => { isAdminRef.current = isAdmin || isSuperAdmin; }, [isAdmin, isSuperAdmin]);

  // Establish one persistent connection for the lifetime of the app
  useEffect(() => {
    if (window.__PRERENDER__) return; // build-time headless crawl — don't open a real visitor socket

    const socket = io(API_BASE, {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      // Admins are handled via ActiveNowCard's join_admin event; skip visitor tracking
      if (isAdminRef.current) return;
      socket.emit('identify', {
        sessionId:  getSessionId(),
        visitorId:  getVisitorId(),
        isLoggedIn: !!userRef.current,
        page:       pageRef.current,
      });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  // Push updates whenever the route or auth state changes
  useEffect(() => {
    if (isAdmin || isSuperAdmin) return;
    if (socketRef.current?.connected) {
      socketRef.current.emit('page_change', {
        page:       location.pathname,
        isLoggedIn: !!user,
      });
    }
  }, [location.pathname, user, isAdmin, isSuperAdmin]);
};

export default useSocketTracker;
